<template>
  <div class="execution-followup">
    <v-card class="ma-sm-3" outlined>
      <tab-header
        :esv="esv"
        :readonly="readonly"
        :lockLoading="lockLoading"
        action="ExecutionFollowUp"
        :isConfirmingTabChange="isConfirmingTabChange"
        :edit-mode-exit-message="editModeExitMessage"
        @edit="$emit('edit')"
      >
        <template #append-title>
          <span class="text-subtitle-1">
            Service Grade: {{ esv.serviceGrade }}
          </span>
        </template>
      </tab-header>
      <edit-unit
        :unitId="unitId"
        :esvId="esvId"
        :readonly="readonly || !$can('AddUnit', 'ESV')"
        :saveCounter="saveCounter"
        @update="getUnits"
      ></edit-unit>
      <v-card class="ma-sm-3" outlined>
        <v-card-title class="text-subtitle-1 grey lighten-3 text-break py-2">
          Search options
        </v-card-title>
        <v-card-text class="mt-3">
          <v-row no-gutters>
            <v-col
              cols="12"
              sm="4"
              :class="$vuetify.breakpoint.smAndUp ? 'pr-1' : 'pb-2'"
            >
              <v-select
                v-model="search['organisation']"
                hide-details="auto"
                :items="availableOrganisations"
                label="Affiliate/ Distributor"
                outlined
                dense
                clearable
                no-data-text="Available organisations could not be fetched, or none are available"
              ></v-select>
            </v-col>
            <v-col
              cols="12"
              sm="3"
              :class="$vuetify.breakpoint.smAndUp ? 'px-1' : 'pb-2'"
            >
              <v-select
                v-model="search['executionStatus']"
                hide-details="auto"
                :items="executionStatusses"
                label="Execution Status"
                outlined
                dense
                clearable
              ></v-select>
            </v-col>
            <v-col
              cols="12"
              sm="3"
              :class="$vuetify.breakpoint.smAndUp ? 'px-1' : 'pb-2'"
            >
              <v-select
                v-model="search['localInChargePerson']"
                hide-details="auto"
                :items="localInChargePersons"
                label="LocalInChargePerson"
                outlined
                dense
                clearable
                no-data-text="No local people assigned"
              ></v-select>
            </v-col>
            <v-col
              cols="12"
              sm="2"
              :class="$vuetify.breakpoint.smAndUp ? 'pl-1' : ''"
            >
              <v-btn height="100%" block small @click="getUnits" color="primary">
                <v-icon left>mdi-magnify</v-icon>
                Search
              </v-btn>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>
      <data-table
        ref="table"
        :optionsPassed="options"
        :totalItems="totalItems"
        :items="items"
        :searchPassed="search"
        :headerSelects="headerSelects"
        :headersTable="headersTable"
        v-on:getItems="getUnits"
        :loading="loading.table"
        @click:row="onRowClicked"
        :item-class="() => 'cursor-pointer'"
      >
        <template #title>
          <v-icon left>mdi-filter</v-icon>Overview
          <v-spacer></v-spacer>
          <v-btn v-if="!readonly && $can('UploadUnitDefinitions', esv)" color="success" @click="downloadTemplate" small class="mr-1" :loading="loading.exportTemplate">
            Download Unit definitions template
          </v-btn>
          <upload-button v-if="!readonly && $can('UploadUnitDefinitions', esv)" @upload="postExecutionFollowUp" :loading="uploadLoading['unitsTemplate']" class="mr-1" ref="fileInput">
              Upload unit definitions
          </upload-button>
          <upload-button v-if="!readonly && $can('UploadUpdatedUnitDefinitions', esv)" @upload="postExecutionFollowUpUpdate" :loading="uploadLoading['unitsUpdateTemplate']" class="mr-1">
              Upload updated unit definitions
          </upload-button>
          <v-btn v-if="$can('ExportToExcel', 'ExecutionFollowUp')" color="success" @click="exportUnitsToExcel" small class="mr-1" :loading="loading.exportUnits">
            Export Units to Excel
          </v-btn>
          <v-btn v-if="!readonly && ($can('RemoveAllUnits', esv) || $can('RemoveAllUnitsOwnOrg', esv))" color="error darken-1" @click="deleteAllUnits" small :loading="loading.removeAll">
            Remove all units
          </v-btn>
        </template>

        <template v-if="!readonly && $can('RemoveUnit', esv)" v-slot:actions="{ item }">
                <v-btn icon color="red" @click="deleteUnit(item)">
                  <v-icon> mdi-delete </v-icon>
                </v-btn>
        </template>
      </data-table>
    </v-card>

    <approve-reject
      :esvId="esvId"
      v-if="$can('Approve', esv)"
    ></approve-reject>
    <publish-footer :esv="esv"></publish-footer>

    <tab-footer
      v-if="!readonly" 
      :esv="esv"
      :showSave="false"
      :showStartApproval="false"
      :showCancel="true"
      cancelText="Finish Editing"
      cancelIcon="mdi-lock-open-variant"
      @cancel="$emit('edit')"
    >
    </tab-footer>

    <upload-report
        :dialog="uploadReportDialog"
        :uploadReport="uploadReport"
        @close="uploadReportDialog = false"
      ></upload-report>
  </div>
</template>

<script>
// @ is an alias to /src
import { ExportType } from "@/enums";
import TabHeader from "./TabHeader.vue";
import TabFooter from "./TabFooter.vue";
import unsavedChangesWarningMixin from '@/mixins/unsavedChangesWarningMixin';

export default {
  name: "ExecutionFollowUp",
  mixins: [unsavedChangesWarningMixin],
  components: {
    EditUnit: () => import("@/components/Unit/Edit"),
    DataTable: () => import("@/components/common/DataTable"),
    TabHeader,
    TabFooter,
    ApproveReject: () => import("@/components/ESV/ApproveReject.vue"),
    PublishFooter: () => import("@/components/ESV/PublishFooter.vue"),
    UploadReport: () => import("@/components/Upload/UploadReport.vue"),
    UploadButton: () => import("@/components/common/UploadButton.vue"),
  },
  props: {
    esvId: Number,
    readonly: {
      type: Boolean,
      default: true,
    },
    lockLoading: Boolean,
    saveCounter: Number,
    reloadCounter: Number,
    isConfirmingTabChange: Boolean,
    editModeExitMessage: {
        type: String,
        default: '<strong>Editing Mode:</strong> This document is locked for you. Please use <strong>Save</strong> or <strong>Cancel</strong> below to finish and release the lock for others.'
    }
  },
  data: () => ({
    unitLoading: false,
    esv: {},
    showMoreDetails: false,
    unitId: null,
    items: [],
    search: [],
    options: {
      /*itemsPerPage: 15*/
    },
    totalItems: 0,
    headerSelects: [{ serviceRequestType: [] }, { status: [] }],
    headersTable: [
      {
        text: "Product",
        value: "product",
        type: "text",
        class: "text-subtitle-1 font-weight-bold",
      },
      {
        text: "Serial",
        value: "serial",
        type: "text",
        class: "text-subtitle-1 font-weight-bold",
      },
      {
        text: "Execution Status",
        value: "executionStatus",
        class: "text-subtitle-1 font-weight-bold",
      },
      {
        text: "Customer Name",
        value: "customerName",
        type: "text",
        class: "text-subtitle-1 font-weight-bold",
      },
      {
        value: "actions",
      },
    ],

    //dropdown
    availableOrganisations: [],
    executionStatusses: [],
    localInChargePersons: [],

    uploadReport: {},
    uploadReportDialog: false,
    uploadLoading: {
      unitsTemplate: false,
      unitsUpdateTemplate: false
    },
    loading: {
      table: false,
      exportUnits: false,
      exportTemplate: false,
      removeAll: false
    }
  }),
  methods: {
    init() {
      this.getEsv();
      this.getAvailableOrganisations();
      this.getExecutionStatusses();
      this.getLocalInChargePersons();
    },
    onRowClicked(row) {
      this.unitId = null;
      this.$nextTick(() => {
        this.unitId = row.id;
      });
    },
    getEsv() {
      this.$api
        .getEsvMinimal(this.esvId)
        .then((resp) => (this.esv = resp.data));
    },
    getUnits(tableSearch) {
      this.loading.table = true;
      if (!tableSearch || !tableSearch.options || !tableSearch.search) {
        tableSearch = {};
        tableSearch.options = this.$refs.table.options;
        tableSearch.search = this.$refs.table.search;
      }
      this.search = tableSearch.search;
      this.options = tableSearch.options;
      tableSearch.id = this.esvId;
      this.$api
        .getUnits(tableSearch)
        .then((resp) => {
          this.items = resp.data.items;
          this.totalItems = resp.data.totalItems;
        })
        .finally(() => (this.loading.table = false));
    },
    getAvailableOrganisations() {
      this.$api
        .getAvailableOrganisations(this.esvId)
        .then((resp) => (this.availableOrganisations = resp.data));
    },
    getExecutionStatusses() {
      this.$api.getExecutionStatusses().then((resp) => {
        this.executionStatusses = resp.data;
      });
    },
    getLocalInChargePersons() {
      this.$api
        .getLocalInChargePersons(this.esvId)
        .then((resp) => (this.localInChargePersons = resp.data));
    },
    downloadTemplate() {
      this.loading.exportTemplate = true;
      this.$api.downloadUnitsInScopeTemplate(this.esvId, ExportType.excel)
        .finally(() => this.loading.exportTemplate = false);
    },
    exportUnitsToExcel() {
      this.loading.exportUnits = true;
      this.$api.downloadUnits(this.esvId, ExportType.excel, { options: this.$refs.table.options, search: this.$refs.table.search })
        .finally(() => this.loading.exportUnits = false);
    },  
    
    postExecutionFollowUp(file) {
      const formData = new FormData();
      formData.append("Id", this.esvId);
      formData.append("File", file);
      this.uploadLoading['unitsTemplate'] = true;

      return this.$api
        .postUnitsTemplate(formData)
        .then((resp) => {
          this.uploadReport = resp.data;
          this.uploadReportDialog = true;
          this.getUnits();
        })
        .finally(() => {
          this.uploadLoading['unitsTemplate'] = false;
          this.resetFileInput();
        });
    },

    resetFileInput() {
      const fileInput = this.$refs.fileInput.$el.querySelector("input[type='file']");
      if (fileInput) {
        fileInput.value = null;
      }
    },

    postExecutionFollowUpUpdate(file) {
      var formData = new FormData();
      formData.append("Id", this.esvId);
      formData.append("File", file);
      this.uploadLoading['unitsUpdateTemplate'] = true;

      return this.$api.postUnitsUpdateTemplate(formData).then((resp) => {
        this.uploadReport = resp.data;
        this.uploadReportDialog = true;
        this.getUnits();
      })
      .finally(() => this.uploadLoading['unitsUpdateTemplate'] = false);
    },

    deleteUnit(unit) {
      this.$confirm('Are you sure you want to delete this unit?')
        .then(res => {
          if (res) {
            this.$api.deleteUnit(unit.id)
              .then(() => this.getUnits())
          }
        })
    },
    deleteAllUnits() {
      this.$confirm('Are you sure you want to delete all units in scope?')
        .then(res => {
          if (res) {
            this.loading.removeAll = true;
            this.$api.deleteAllUnits(this.esvId)
              .then(() => this.getUnits())
              .finally(() => this.loading.removeAll = false);
          }
        })
    }
  },
  created() {
    this.init();
  },
  watch: {
    // readonly: {
    //   handler() {
    //     if (this.readonly) {
    //       this.init();
    //     }
    //   },
    // },
    reloadCounter: {
      handler() {
        console.log('reload ex fu');
        this.init();
      }
    }
  },
};
</script>